import { useEffect, useState } from "react";
import "./PerformanceSummary.css";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  ResponsiveContainer,
  LabelList,
  CartesianGrid,
} from "recharts";
import axios from "./../../../axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilterCircleXmark } from "@fortawesome/free-solid-svg-icons";

const COLORS = [
  "#FF4D4D",
  "#FF9900",
  "#FFD700",
  "#66CC66",
  "#339933",
  "#3366FF",
  "#9933FF",
];

function PerformanceSummaryPage({
  student,
  startDate,
  endDate,
  subjects,
  assessmentTypeId,
  subjectWiseData,
  allSubjectList,
}) {
  const [overallPercentage, setoverallPercentage] = useState([]);
  const [supplementData, setSupplementData] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedSupplement, setSelectedSupplement] = useState("");

  useEffect(() => {
    if (!startDate || !endDate || !student?.id) return;
    const fetchOverallPercentage = async () => {
      try {
        const response = await axios.get(
          `api/v1/performance/student/${student?.id}/overallAverage/get?startDate=${startDate}&endDate=${endDate}&assessmentEventId=${assessmentTypeId}`,
        );
        setoverallPercentage(response?.data?.data);
      } catch (error) {
        console.log("error", error);
      }
    };
    fetchOverallPercentage();
  }, [assessmentTypeId]);

  useEffect(() => {
    if (
      !startDate ||
      !endDate ||
      !student?.id ||
      !student?.standard?.id ||
      !student?.section?.id
    )
      return;
    const fetchSupplementData = async () => {
      try {
        const response = await axios.get(
          `api/v1/performance/standard/${student?.standard?.id}/section/${
            student?.section?.id
          }/student/${
            student?.id
          }/percentageWithSupplementType/get?startDate=${startDate}&endDate=${endDate}&subjectId=${""}&assessmentEventId=${assessmentTypeId}`,
        );
        setSupplementData(response?.data?.data);
      } catch (error) {
        console.log("error", error);
      }
    };
    fetchSupplementData();
  }, [selectedSubject, assessmentTypeId]);

  const overallScore = overallPercentage[0]?.averagePercentageScore
    ? Math.round(overallPercentage[0].averagePercentageScore)
    : 0;

  const data = [
    { name: "Score", value: overallScore },
    { name: "Remaining", value: Math.max(100 - overallScore, 0) },
  ];

  const reduceParaGraphlenght = (text, length) => {
    if (text.length <= length) {
      return text;
    }
    return text.slice(0, length) + "...";
  };

  const subjectsData =
    subjectWiseData?.map((subject) => ({
      name: subject?.result[0]?.name,
      assessment: subject?.averagePercentageScore,
      fill: subject?.result[0]?.color || "6dc6f2", // Assign color here
    })) || [];

  const selectedSubjectName = allSubjectList?.find(
    (s) => s.id === selectedSubject,
  )?.name;

  const rawData = Array.isArray(supplementData)
    ? supplementData?.reduce(
        (acc, supplement) => {
          const subjectName =
            supplement?.subjectDetails?.[0]?.name || "Unspecified";
          const percentageMarks =
            typeof supplement?.percentageMarks === "number"
              ? supplement.percentageMarks.toFixed(1)
              : 0;
          const color = supplement?.subjectDetails?.[0]?.color || "#999999"; // fallback grey

          acc[subjectName] = {
            percentage: percentageMarks,
            color: `#${color}`, // ensure proper hex
          };
          return acc;
        },
        { category: "Assessment" },
      )
    : { category: "Assessment" };

  const subjectKeys = Object.keys(rawData).filter((key) => key !== "category");
  const numSubjects = subjectKeys.length || 1;

  const performanceCategoryData = [
    subjectKeys?.reduce(
      (acc, key) => {
        acc[key] = rawData[key].percentage;
        return acc;
      },
      { category: "Assessment" },
    ),
  ];

  const sortedPerformanceCategoryData = [...performanceCategoryData].sort(
    (a, b) => {
      const totalA = subjectKeys.reduce((sum, key) => sum + (a[key] || 0), 0);
      const totalB = subjectKeys.reduce((sum, key) => sum + (b[key] || 0), 0);
      return totalB - totalA; // Correct descending order
    },
  );

  const subjectColorsMap = subjectKeys.reduce((acc, key) => {
    acc[key] = rawData[key].color;
    return acc;
  }, {});

  return (
    <div className="chart-card mt-2">
      <div className="card-content-summary">
        <div className="p-3 card-title">
          {student?.name} Performance Summary
        </div>
        <div className="row g-3 p-4">
          {/* Pie Chart */}
          <div className="col-12 col-md-3">
            <div className="border rounded h-100 position-relative">
              <div
                style={{ backgroundColor: "#edeef0", padding: "20px" }}
                className="h-10 w-100"
              >
                <span className="poppins-semibold">Overall Performance</span>
              </div>
              <div
                className="d-flex justify-content-center align-items-center position-relative"
                style={{ height: "250px" }}
              >
                <ResponsiveContainer width={250} height={250}>
                  <PieChart>
                    <Pie
                      data={data}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      startAngle={230}
                      endAngle={-50}
                      dataKey="value"
                    >
                      {data.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={index === 0 ? COLORS[4] : "#ccc"}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div
                  className="position-absolute top-50 start-50 translate-middle fw-bold"
                  style={{ fontSize: "1.5rem" }}
                >
                  {overallScore.toFixed(1)}%
                </div>
              </div>
            </div>
          </div>

          {/* Subjects Bar Chart */}
          <div className="col-12 col-md-4">
            <div className="border rounded bg-white h-100">
              <div
                className="d-flex justify-content-between align-items-center flex-wrap mb-2 p-3 h-10"
                style={{ backgroundColor: "#edeef0" }}
              >
                <span className="poppins-semibold">Subjects</span>
                <div className="d-flex gap-2 flex-row align-items-center ">
                  <select
                    className="form-select form-select-sm"
                    value={"assessment"}
                    onChange={(e) => setSelectedSupplement(e.target.value)}
                  >
                    <option value="assessment">Assessment</option>
                  </select>
                  {/* <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setSelectedSupplement("")}
                    title="Clear Filter"
                  >
                    <FontAwesomeIcon icon={faFilterCircleXmark} />
                  </button> */}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={subjectsData}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} />
                  <Tooltip
                    content={({ payload }) => {
                      if (payload && payload.length) {
                        const value = payload[0].value;
                        return (
                          <div
                            style={{
                              background: "#fff",
                              border: "1px solid #ccc",
                              padding: "5px 10px",
                              fontSize: "15px",
                            }}
                          >
                            {value.toFixed(1)}%
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="assessment"
                    stackId="a"
                    fill="#000000"
                    barSize={30}
                  >
                    {subjectsData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={`#${entry.fill}`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Supplement Bar Chart */}
          <div className="col-12 col-md-5">
            <div className=" border rounded bg-white h-100">
              <div
                className="d-flex gap-4 justify-content-between align-items-center  mb-2 p-3 h-10"
                style={{ backgroundColor: "#edeef0" }}
              >
                <h5 className="mb-0">Supplements</h5>
                <div className="d-flex gap-1 align-items-center flex-row">
                  <select
                    className="form-select form-select-sm"
                    value={selectedSubject}
                    onChange={(e) => setSelectedSubject(e.target.value)}
                  >
                    <option value="">Subject Name</option>
                    {allSubjectList?.map((subject, index) => (
                      <option key={subject.id} value={subject.id}>
                        {subject.name}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setSelectedSubject("")}
                    title="Clear Filter"
                  >
                    <FontAwesomeIcon icon={faFilterCircleXmark} />
                  </button>
                </div>
              </div>

              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={sortedPerformanceCategoryData}
                  barCategoryGap="15%"
                  barGap={4}
                >
                  {/* X Axis with category + subject name */}
                  <XAxis
                    dataKey="category"
                    tickFormatter={(value) =>
                      selectedSubject
                        ? `${value} (${selectedSubjectName})`
                        : value
                    }
                  />

                  <CartesianGrid vertical={false} />

                  {/* Default Recharts Tooltip */}
                  <Tooltip formatter={(value, name) => [`${value}%`, name]} />

                  {/* Default Recharts Legend */}
                  <Legend />

                  {(selectedSubject
                    ? [selectedSubjectName]
                    : subjectKeys.slice().sort((a, b) => {
                        const valA = sortedPerformanceCategoryData[0][a] || 0;
                        const valB = sortedPerformanceCategoryData[0][b] || 0;
                        return valA - valB;
                      })
                  ).map((key) => (
                    <Bar
                      key={key}
                      dataKey={key}
                      fill={subjectColorsMap[key]}
                      stackId="a"
                      barSize={60} // wider bar
                    >
                      <LabelList
                        dataKey={key}
                        position="inside"
                        fill="#fff"
                        formatter={(value) => `${value}%`}
                      />
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PerformanceSummaryPage;
